import { useEffect, useState, useRef } from "react";
import {
  Activity,
  Cpu as CpuIcon,
  HardDrive,
  Zap,
  ArrowDown,
  ArrowUp,
  Clock,
} from "lucide-react";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  AreaChart,
  Area,
} from "recharts";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";

const MAX_HISTORY = 60;

function formatUptime(seconds) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${d}d ${h}h ${m}m ${s}s`;
}

function Widget({ title, children, testId, right }) {
  return (
    <div
      data-testid={testId}
      className="bg-[#0a0a0a]"
      style={{ border: "1px solid #1f1f22" }}
    >
      <div
        className="px-4 py-2.5 flex items-center justify-between"
        style={{ borderBottom: "1px solid #1f1f22" }}
      >
        <span className="overline">{title}</span>
        {right}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function BigMetric({ value, unit, sub }) {
  return (
    <div>
      <div className="font-mono text-4xl text-white leading-none">
        {value}
        <span className="text-lg text-[#8b8d98] ml-1">{unit}</span>
      </div>
      {sub ? <div className="mt-2 text-xs text-[#8b8d98] font-mono">{sub}</div> : null}
    </div>
  );
}

export default function Dashboard() {
  const [info, setInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);
  const [netDelta, setNetDelta] = useState({ sent: 0, recv: 0 });
  const prev = useRef({ sent: 0, recv: 0, ts: 0 });

  useEffect(() => {
    api
      .get("/system/info")
      .then((r) => setInfo(r.data))
      .catch((e) => setErr(formatApiError(e)));
  }, []);

  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/system/stats");
        if (!mounted) return;
        setStats(data);
        setErr("");
        const now = Date.now();
        const tPrev = prev.current;
        if (tPrev.ts) {
          const dt = (now - tPrev.ts) / 1000 || 1;
          setNetDelta({
            sent: Math.max(0, (data.network.bytes_sent - tPrev.sent) / dt),
            recv: Math.max(0, (data.network.bytes_recv - tPrev.recv) / dt),
          });
        }
        prev.current = {
          sent: data.network.bytes_sent,
          recv: data.network.bytes_recv,
          ts: now,
        };
        setHistory((h) => {
          const next = [
            ...h,
            {
              t: new Date().toLocaleTimeString(),
              cpu: data.cpu.overall,
              mem: data.memory.percent,
            },
          ];
          return next.slice(-MAX_HISTORY);
        });
      } catch (e) {
        setErr(formatApiError(e));
      }
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  if (!stats) {
    return (
      <div className="p-10 text-[#8b8d98] font-mono text-xs tracking-[0.2em] uppercase animate-pulse">
        Lade Metriken…
      </div>
    );
  }

  const fmtBps = (bps) => {
    if (bps < 1024) return `${bps.toFixed(0)} B/s`;
    if (bps < 1024 ** 2) return `${(bps / 1024).toFixed(1)} KB/s`;
    if (bps < 1024 ** 3) return `${(bps / 1024 ** 2).toFixed(1)} MB/s`;
    return `${(bps / 1024 ** 3).toFixed(2)} GB/s`;
  };

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle="// live metrics" testId="dashboard-header">
        <div className="hidden sm:flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[#8b8d98]">
          <span className="w-2 h-2 rounded-full bg-[#10b981] pulse-dot" />
          {info?.hostname || "server"}
        </div>
      </PageHeader>

      {err ? (
        <div className="mx-8 mt-6 text-xs font-mono text-[#dc2626] border border-[#dc2626]/40 bg-[#dc2626]/5 px-3 py-2">
          {err}
        </div>
      ) : null}

      <div className="p-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-px bg-[#1f1f22]">
        <Widget title="// cpu usage" testId="widget-cpu">
          <BigMetric
            value={stats.cpu.overall.toFixed(1)}
            unit="%"
            sub={`Load: ${stats.cpu.load_avg["1m"].toFixed(2)} · ${stats.cpu.load_avg["5m"].toFixed(2)} · ${stats.cpu.load_avg["15m"].toFixed(2)}`}
          />
          <div className="mt-4 flex flex-wrap gap-1">
            {stats.cpu.per_core.map((v, i) => (
              <div
                key={i}
                title={`Core ${i}: ${v.toFixed(1)}%`}
                className="flex-1 min-w-[24px] h-1.5 bg-[#1f1f22] relative"
              >
                <div
                  className="h-full bg-[#22d3ee]"
                  style={{ width: `${Math.min(100, v)}%` }}
                />
              </div>
            ))}
          </div>
        </Widget>

        <Widget title="// memory" testId="widget-memory">
          <BigMetric
            value={stats.memory.percent.toFixed(1)}
            unit="%"
            sub={`${stats.memory.used_human} / ${stats.memory.total_human}`}
          />
          <div className="mt-4 h-1.5 bg-[#1f1f22]">
            <div
              className="h-full bg-[#10b981]"
              style={{ width: `${stats.memory.percent}%` }}
            />
          </div>
          <div className="mt-3 text-xs text-[#8b8d98] font-mono flex justify-between">
            <span>swap {stats.swap.percent.toFixed(1)}%</span>
            <span>
              {stats.swap.used_human}/{stats.swap.total_human}
            </span>
          </div>
        </Widget>

        <Widget title="// network" testId="widget-network">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <ArrowDown className="w-4 h-4 text-[#22d3ee]" strokeWidth={1.5} />
              <div className="font-mono text-xl text-white">{fmtBps(netDelta.recv)}</div>
            </div>
            <div className="flex items-center gap-3">
              <ArrowUp className="w-4 h-4 text-[#eab308]" strokeWidth={1.5} />
              <div className="font-mono text-xl text-white">{fmtBps(netDelta.sent)}</div>
            </div>
            <div className="text-[11px] font-mono text-[#8b8d98]">
              Σ ↓ {stats.network.bytes_recv_human} · ↑ {stats.network.bytes_sent_human}
            </div>
          </div>
        </Widget>

        <Widget title="// uptime" testId="widget-uptime">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-[#22d3ee] mt-1" strokeWidth={1.5} />
            <div>
              <div className="font-mono text-xl text-white leading-tight">
                {info ? formatUptime(info.uptime_seconds) : "—"}
              </div>
              <div className="mt-2 text-[11px] font-mono text-[#8b8d98]">
                {info?.hostname}
              </div>
              <div className="text-[11px] font-mono text-[#3f3f46]">
                {info?.platform}
              </div>
            </div>
          </div>
        </Widget>
      </div>

      <div className="px-8 pb-4 grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#1f1f22]">
        <Widget title="// cpu history" testId="chart-cpu-history">
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={history} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
                <defs>
                  <linearGradient id="cpuFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="t" hide />
                <YAxis domain={[0, 100]} stroke="#3f3f46" fontSize={10} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#000",
                    border: "1px solid #1f1f22",
                    fontFamily: "IBM Plex Mono",
                    fontSize: 11,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="cpu"
                  stroke="#22d3ee"
                  strokeWidth={1.5}
                  fill="url(#cpuFill)"
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Widget>

        <Widget title="// memory history" testId="chart-mem-history">
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={history} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
                <XAxis dataKey="t" hide />
                <YAxis domain={[0, 100]} stroke="#3f3f46" fontSize={10} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#000",
                    border: "1px solid #1f1f22",
                    fontFamily: "IBM Plex Mono",
                    fontSize: 11,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="mem"
                  stroke="#10b981"
                  strokeWidth={1.5}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Widget>
      </div>

      <div className="px-8 pb-8">
        <Widget title="// disks" testId="widget-disks">
          <div className="divide-y divide-[#1f1f22]">
            <div className="grid grid-cols-12 gap-4 pb-2 overline">
              <div className="col-span-3">Mountpoint</div>
              <div className="col-span-2">FS</div>
              <div className="col-span-5">Belegung</div>
              <div className="col-span-2 text-right">Total</div>
            </div>
            {stats.disks.map((d) => (
              <div
                key={d.mountpoint}
                data-testid={`disk-row-${d.mountpoint}`}
                className="grid grid-cols-12 gap-4 py-2.5 items-center font-mono text-xs"
              >
                <div className="col-span-3 text-white truncate">{d.mountpoint}</div>
                <div className="col-span-2 text-[#8b8d98]">{d.fstype}</div>
                <div className="col-span-5 flex items-center gap-3">
                  <div className="flex-1 h-1.5 bg-[#1f1f22]">
                    <div
                      className={
                        d.percent > 90
                          ? "h-full bg-[#dc2626]"
                          : d.percent > 70
                          ? "h-full bg-[#eab308]"
                          : "h-full bg-[#22d3ee]"
                      }
                      style={{ width: `${d.percent}%` }}
                    />
                  </div>
                  <span className="w-10 text-right text-white">{d.percent.toFixed(0)}%</span>
                </div>
                <div className="col-span-2 text-right text-[#8b8d98]">
                  {d.used_human} / {d.total_human}
                </div>
              </div>
            ))}
          </div>
        </Widget>
      </div>
    </div>
  );
}
